// NexoNurse API utility — all calls delegate to apiFetch (apiClient).
// All authentication is handled by apiFetch via the getToken callback.
//
// Token usage pattern:
//   import { useAuth } from '../contexts/AuthContext';
//   const { getAccessToken } = useAuth();
//   fetchDailyFocus(getAccessToken)
//
// Where AuthContext exposes: getAccessToken = () => accessToken (from state)

import { apiFetch } from '../services/apiClient';

// Transform API signal-shaped emails to the rich email shape InboxContent expects.
// The API now returns persisted email_state fields (isRead, isStarred, folder,
// queueStatus, boardSwimlane, scheduledDate, assignedTo, reclassifiedDomain)
// directly on the response object, so we use those instead of generating defaults.
function signalToEmail(s) {
  const urgencyMap = { 5: 'critical', 4: 'high', 3: 'medium', 2: 'low', 1: 'info' };
  const urgencyLabel = urgencyMap[s.urgency] || 'medium';
  return {
    id: s.id,
    threadId: s.threadId || s.id,
    from: s.sender || s.from || { name: s.domain + '_system', email: s.domain + '_system@nexonurse.com' },
    to: [{ name: 'Practice Manager', email: 'pm@nexonurse.com' }],
    cc: [],
    subject: s.subject || `Signal: ${s.preview?.substring(0, 60) || 'Untitled'}`,
    preview: s.preview || '',
    bodyHtml: `<p>${s.preview || ''}</p>`,
    bodyText: s.preview || '',
    attachments: s.attachments || [],
    receivedAt: s.receivedAt || new Date().toISOString(),
    isRead: s.isRead ?? (s.unread === false),
    isStarred: s.isStarred ?? (s.urgency >= 4),
    folder: s.folder || 'inbox',
    labels: s.urgency >= 4 ? ['urgent'] : [],
    primaryDomain: s.reclassifiedDomain || s.domain || 'operations',
    secondaryDomains: s.secondaryDomains || [],
    domains: [
      { domain: s.domain || 'operations', confidence: 90 },
      ...(s.secondaryDomains || []).map(d => ({ domain: d, confidence: 70 })),
    ],
    urgency: urgencyLabel,
    urgencyScore: s.urgency || 3,
    queueStatus: s.queueStatus ?? null,
    boardSwimlane: s.boardSwimlane ?? null,
    scheduledDate: s.scheduledDate ?? null,
    assignedTo: s.assignedTo ?? null,
    reclassifiedDomain: s.reclassifiedDomain ?? null,
    messageCount: s.messageCount || 1,
    subcategory: s.subcategory || null,
  };
}

// ─── USER ─────────────────────────────────────────────────────────────────────
export async function fetchUser(getToken) {
  return apiFetch('/api/metrics/user', {}, getToken);
}

// ─── PRIORITY QUEUE ───────────────────────────────────────────────────────────
export async function fetchPriorityQueue(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/queue${query}`, {}, getToken);
}

// ─── HUDDLE BRIEF ─────────────────────────────────────────────────────────────
export async function fetchHuddleBrief(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/huddle-brief${query}`, {}, getToken);
}

// ─── DOMAIN SUMMARY ──────────────────────────────────────────────────────────
export async function fetchDomainSummary(unitId, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/domain-summary${query}`, {}, getToken);
}

// ─── INBOX EMAILS ─────────────────────────────────────────────────────────────
export async function fetchInboxEmails(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  const raw = await apiFetch(`/api/emails${query}`, {}, getToken);
  // If the data is already in email shape (has bodyHtml), return as-is
  if (Array.isArray(raw) && raw.length > 0 && raw[0].bodyHtml) return raw;
  // Transform signal-shaped API data to email shape
  if (Array.isArray(raw)) return raw.map(signalToEmail);
  return raw;
}

// ─── QUALITY METRICS ──────────────────────────────────────────────────────────
export async function fetchQualityMetrics(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/quality${query}`, {}, getToken);
}

// ─── QUALITY & REGULATORY (FQR) ──────────────────────────────────────────────

export async function fetchQROverview(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/overview?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRRegulatory(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/regulatory?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRAccreditation(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/accreditation?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRIncidents(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/incidents?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRHedis(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/hedis?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRInitiatives(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/initiatives?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRSignals(unitId = 'all', getToken) {
  return apiFetch(`/api/quality/signals?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchQRDirectorSites(scopeId, getToken) {
  return apiFetch(`/api/quality/director/sites?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

// ─── POPULATION HEALTH & CARE COORDINATION (NX-008) ─────────────────────────

export async function fetchPHRegistries(unitId = 'all', getToken) {
  return apiFetch(`/api/population-health/registries?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchPHTransitions(unitId = 'all', getToken) {
  return apiFetch(`/api/population-health/transitions?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchPHSdoh(unitId = 'all', getToken) {
  return apiFetch(`/api/population-health/sdoh?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchPHTemplates(unitId = 'all', getToken) {
  return apiFetch(`/api/population-health/templates?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchPHDirectorSites(getToken) {
  return apiFetch('/api/population-health/director/sites', {}, getToken);
}

// ─── STAFFING ─────────────────────────────────────────────────────────────────
export async function fetchStaffing(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/staffing${query}`, {}, getToken);
}

// ─── CONSUMER INSIGHTS ────────────────────────────────────────────────────────
export async function fetchConsumerInsights(unitId = 'all', getToken) {
  return apiFetch(`/api/metrics/consumer?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchConsumerSurveys(unitId = 'all', getToken) {
  return apiFetch(`/api/metrics/consumer/surveys?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchConsumerReputation(unitId = 'all', getToken) {
  return apiFetch(`/api/metrics/consumer/reputation?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchConsumerComplaints(unitId = 'all', getToken) {
  return apiFetch(`/api/metrics/consumer/complaints?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}
export async function fetchConsumerSignals(unitId = 'all', getToken) {
  return apiFetch(`/api/metrics/consumer/signals?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// ─── CONSUMER INSIGHTS — MUTATIONS ───────────────────────────────────────────

export async function updateComplaintStatus(id, status, getToken) {
  return apiFetch(`/api/metrics/consumer/complaints/${encodeURIComponent(id)}/status`, {
    method: 'PUT', body: JSON.stringify({ status }),
  }, getToken);
}

export async function addComplaintNote(id, text, getToken) {
  return apiFetch(`/api/metrics/consumer/complaints/${encodeURIComponent(id)}/notes`, {
    method: 'POST', body: JSON.stringify({ text }),
  }, getToken);
}

export async function dismissConsumerSignal(id, getToken) {
  return apiFetch(`/api/metrics/consumer/signals/${encodeURIComponent(id)}/dismiss`, {
    method: 'PUT',
  }, getToken);
}

// ─── ACCESS METRICS ───────────────────────────────────────────────────────────
export async function fetchAccessMetrics(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/access${query}`, {}, getToken);
}

// ─── STAFFING ALERTS ──────────────────────────────────────────────────────────
export async function fetchStaffingAlerts(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/staffing-alerts${query}`, {}, getToken);
}

// ─── STAFFING TABS (FST.2–FST.6) ─────────────────────────────────────────────
export async function fetchStaffingDashboard(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/dashboard?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingDepartments(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/departments?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingCalendar(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/calendar?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingCredentials(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/credentials?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingAbsencePatterns(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/absence-patterns?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingFTEBudget(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/fte-budget?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchStaffingRoleLibrary(unitId = 'unit-4w', getToken) {
  return apiFetch(`/api/staffing/role-library?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// ─── STAFFING — NEW API FUNCTIONS (NX-009 Wave 3) ──────────────────────────

export async function fetchAuthRoles(getToken) {
  return apiFetch('/api/roles/auth', {}, getToken);
}

export async function fetchOperationalRoles(getToken) {
  return apiFetch('/api/roles/operational', {}, getToken);
}

export async function createOperationalRole(data, getToken) {
  return apiFetch('/api/roles/operational', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function updateOperationalRole(roleId, data, getToken) {
  return apiFetch(`/api/roles/operational/${roleId}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function fetchRoleVersionHistory(roleId, getToken) {
  return apiFetch(`/api/roles/operational/${roleId}/history`, {}, getToken);
}

export async function fetchTaskTemplates(getToken) {
  const res = await apiFetch('/api/practice-management/tasks', {}, getToken);
  return (res ?? []).filter((t) => t.kind === 'template');
}

export async function fetchOperationalRoleDefaultTasks(roleId, getToken) {
  return apiFetch(`/api/roles/operational/${roleId}/default-tasks`, {}, getToken);
}

export async function updateTaskTemplateOwner(taskId, operationalRoleId, getToken) {
  return apiFetch(`/api/practice-management/templates/${taskId}/owner`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operationalRoleId }),
  }, getToken);
}

export async function fetchDailyBindings(date, siteId, getToken) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (siteId) params.set('siteId', siteId);
  return apiFetch(`/api/staffing/bindings?${params}`, {}, getToken);
}

export async function assignBinding(data, getToken) {
  return apiFetch('/api/staffing/bindings', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function fetchAvailableStaff(roleSlug, getToken) {
  const params = roleSlug ? `?role=${encodeURIComponent(roleSlug)}` : '';
  return apiFetch(`/api/staffing/available-staff${params}`, {}, getToken);
}

export async function logAbsence(data, getToken) {
  return apiFetch('/api/staffing/absences', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function fetchAbsences(siteId, startDate, endDate, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return apiFetch(`/api/staffing/absences?${params}`, {}, getToken);
}

// uploadStaffingCSV — REMOVED (ADR-046, Phase 3).
// The /api/staffing/ingestion/csv-upload route returns 410 Gone. Use the
// presigned upload flow instead: fetchCSVPresignUrl → PUT to S3 → confirmCSVUpload.
// See DataIngestionPanel.jsx for the canonical example.

export async function fetchImportBatches(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/staffing/ingestion/batches${query}`, {}, getToken);
}

export async function fetchFeedHealth(getToken, unitId = 'all') {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/staffing/ingestion/feed-health${query}`, {}, getToken);
}

export async function fetchCSVPresignUrl(domain, filename, getToken, orgUnitId = null) {
  const body = { dataDomain: domain, filename };
  if (orgUnitId && orgUnitId !== 'all') body.orgUnitId = orgUnitId;
  return apiFetch(
    `/api/staffing/ingestion/csv-presign`,
    { method: 'POST', body: JSON.stringify(body) },
    getToken,
  );
}

export async function confirmCSVUpload(batchId, getToken) {
  return apiFetch(
    `/api/staffing/ingestion/csv-confirm`,
    { method: 'POST', body: JSON.stringify({ batchId }) },
    getToken,
  );
}

// ─── STAFFING — VACANCY & STRUCTURAL GAP (NX-009-13, NX-009-14) ────────────

export async function fetchVacancyIndex(siteId, getToken) {
  return apiFetch(`/api/staffing/vacancy-index?siteId=${encodeURIComponent(siteId || '')}`, {}, getToken);
}

export async function fetchStructuralVacancies(siteId, getToken) {
  return apiFetch(`/api/staffing/structural-vacancies?siteId=${encodeURIComponent(siteId || '')}`, {}, getToken);
}

export async function escalateOnboarding(userId, reason, getToken) {
  return apiFetch(
    `/api/staffing/onboarding/escalate`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId, reason }) },
    getToken,
  );
}

// ─── STAFFING — CREDENTIAL PIPELINE (NX-009-10) ────────────────────────────

export async function fetchCredentialPipeline(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/staffing/credentials/pipeline${query}`, {}, getToken);
}

export async function fetchCredentialComplianceTrend(unitId, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/staffing/credentials/compliance-trend${query}`, {}, getToken);
}

export async function updateCredentialRenewalStatus(credentialId, renewalStatus, getToken) {
  return apiFetch(`/api/staffing/credentials/${credentialId}/renewal-status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ renewalStatus }),
  }, getToken);
}

// ─── STAFFING — STAFF LIFECYCLE (NX-009-11) ────────────────────────────────

export async function fetchStaffLifecycle(status, getToken) {
  const params = status && status !== 'all' ? `?status=${status}` : '';
  return apiFetch(`/api/staffing/staff/lifecycle${params}`, {}, getToken);
}

export async function fetchStaffAuditTrail(userId, getToken) {
  return apiFetch(`/api/staffing/staff/${userId}/audit-trail`, {}, getToken);
}

export async function deactivateStaff(userId, data, getToken) {
  return apiFetch(`/api/staffing/staff/${userId}/deactivate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }, getToken);
}

export async function reactivateStaff(userId, getToken) {
  return apiFetch(
    `/api/staffing/staff/${userId}/reactivate`,
    { method: 'POST', body: JSON.stringify({}) },
    getToken,
  );
}

// ─── STAFFING — INTELLIGENCE PANELS (NX-009 Wave 5) ────────────────────────

export async function fetchStaffingRatios(orgUnitId, getToken) {
  return apiFetch(`/api/staffing/ratios?orgUnitId=${encodeURIComponent(orgUnitId || '')}`, {}, getToken);
}

export async function createStaffingRatio(data, getToken) {
  return apiFetch('/api/staffing/ratios', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function updateStaffingRatio(id, data, getToken) {
  return apiFetch(`/api/staffing/ratios/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function deleteStaffingRatio(id, getToken) {
  return apiFetch(`/api/staffing/ratios/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }, getToken);
}

export async function fetchCompliance(siteId, date, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (date) params.set('date', date);
  return apiFetch(`/api/staffing/compliance?${params}`, {}, getToken);
}

export async function fetchBreaches(siteId, date, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (date) params.set('date', date);
  return apiFetch(`/api/staffing/breaches?${params}`, {}, getToken);
}

export async function applyRatioOverride(ratioId, data, getToken) {
  return apiFetch(`/api/staffing/ratios/${encodeURIComponent(ratioId)}/override`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function fetchOTIndividual(userId, periodStart, periodEnd, getToken) {
  const params = new URLSearchParams();
  if (userId) params.set('userId', userId);
  if (periodStart) params.set('periodStart', periodStart);
  if (periodEnd) params.set('periodEnd', periodEnd);
  return apiFetch(`/api/staffing/ot/individual?${params}`, {}, getToken);
}

export async function fetchOTSiteSummary(siteId, periodStart, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (periodStart) params.set('periodStart', periodStart);
  return apiFetch(`/api/staffing/ot/site-summary?${params}`, {}, getToken);
}

export async function fetchOTBreaches(siteId, periodStart, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (periodStart) params.set('periodStart', periodStart);
  return apiFetch(`/api/staffing/ot/breaches?${params}`, {}, getToken);
}

export async function fetchPairings(providerId, getToken) {
  return apiFetch(`/api/staffing/pairings?providerId=${encodeURIComponent(providerId || '')}`, {}, getToken);
}

export async function createPairing(data, getToken) {
  return apiFetch('/api/staffing/pairings', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

export async function deletePairing(id, getToken) {
  return apiFetch(`/api/staffing/pairings/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }, getToken);
}

export async function fetchConflicts(siteId, date, getToken) {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  if (date) params.set('date', date);
  return apiFetch(`/api/staffing/conflicts?${params}`, {}, getToken);
}

export async function resolveConflict(id, data, getToken) {
  return apiFetch(`/api/staffing/conflicts/${encodeURIComponent(id)}/resolve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }, getToken);
}

// ─── DIRECTOR STAFFING ──────────────────────────────────────────────────────

export async function fetchDirectorStaffingDashboard(scopeId, getToken) {
  return apiFetch(`/api/staffing/director/dashboard?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

export async function fetchDirectorRoleAssignments(scopeId, getToken) {
  return apiFetch(`/api/staffing/director/roles?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

export async function fetchDirectorCredentials(scopeId, getToken) {
  return apiFetch(`/api/staffing/director/credentials?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

export async function fetchDirectorAbsencePatterns(scopeId, getToken) {
  return apiFetch(`/api/staffing/director/absence?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

export async function fetchDirectorFTEBudget(scopeId, getToken) {
  return apiFetch(`/api/staffing/director/fte-budget?scopeId=${encodeURIComponent(scopeId || '')}`, {}, getToken);
}

export async function fetchDirectorComplianceSummary(scopeId, getToken) {
  const params = scopeId ? `?scopeId=${scopeId}` : '';
  return apiFetch(`/api/staffing/director/compliance-summary${params}`, {}, getToken);
}

export async function fetchDirectorStructuralVacancies(scopeId, getToken) {
  const params = scopeId ? `?scopeId=${scopeId}` : '';
  return apiFetch(`/api/staffing/director/structural-vacancies${params}`, {}, getToken);
}

// ─── WEEKLY PRIORITIES ──────────────────────────────────────────────────────
export async function fetchWeeklyPriorities(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/weekly-priorities${query}`, {}, getToken);
}

// ─── PERFORMANCE DISTRIBUTION ─────────────────────────────────────────────────
export async function fetchPerformanceDistribution(unitId = 'all', _filters = {}, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/performance-distribution${query}`, {}, getToken);
}

// ─── EXECUTIVE DASHBOARD ──────────────────────────────────────────────────────
export async function fetchExecutiveDashboard(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/executive-dashboard${query}`, {}, getToken);
}

// ─── ROLE MANAGEMENT ──────────────────────────────────────────────────────────
export async function fetchRoles(getToken) {
  return apiFetch('/api/roles', {}, getToken);
}

export async function saveRole(roleData, getToken) {
  return apiFetch('/api/roles', { method: 'POST', body: JSON.stringify(roleData) }, getToken);
}

export async function deleteRole(roleId, getToken) {
  return apiFetch(`/api/roles/${roleId}`, { method: 'DELETE' }, getToken);
}

// ─── CHECKLISTS ───────────────────────────────────────────────────────────────
export async function fetchChecklists(unitId = 'all', filters = {}, getToken) {
  const params = new URLSearchParams();
  if (unitId && unitId !== 'all') params.set('unitId', unitId);
  if (filters.isRecurring !== undefined) params.set('isRecurring', filters.isRecurring);
  const query = params.toString() ? `?${params}` : '';
  return apiFetch(`/api/checklists${query}`, {}, getToken);
}

export async function saveChecklist(checklistData, getToken) {
  return apiFetch('/api/checklists', { method: 'POST', body: JSON.stringify(checklistData) }, getToken);
}

export async function deleteChecklist(checklistId, getToken) {
  return apiFetch(`/api/checklists/${checklistId}`, { method: 'DELETE' }, getToken);
}

export async function getChecklistSubmissions(checklistId, getToken) {
  return apiFetch(`/api/checklists/${checklistId}/submissions`, {}, getToken);
}

export async function submitChecklist(submission, getToken) {
  return apiFetch(`/api/checklists/${submission.checklistId}/submit`, {
    method: 'POST', body: JSON.stringify(submission)
  }, getToken);
}

// ─── ORG HIERARCHY ────────────────────────────────────────────────────────────
export async function fetchOrgHierarchy(getToken) {
  return apiFetch('/api/org-hierarchy', {}, getToken);
}

export async function saveOrgHierarchy(data, getToken) {
  return apiFetch('/api/org-hierarchy', { method: 'PUT', body: JSON.stringify(data) }, getToken);
}

// ─── KPI VALUES ──────────────────────────────────────────────────────────────
export async function fetchKPIs(domain, unitId, getToken) {
  const params = new URLSearchParams();
  if (domain) params.set('domain', domain);
  if (unitId && unitId !== 'all') params.set('unitId', unitId);
  const query = params.toString() ? `?${params}` : '';
  return apiFetch(`/api/metrics/kpis${query}`, {}, getToken);
}

// ─── PRACTICE OPERATIONS (OpsRibbon) ─────────────────────────────────────────
export async function fetchTaskSummary(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/task-summary${query}`, {}, getToken);
}

// ─── RELIABILITY (ReliabilityBar) ────────────────────────────────────────────
export async function fetchReliability(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/metrics/reliability${query}`, {}, getToken);
}

// ─── DAILY FOCUS (signals inbox) ─────────────────────────────────────────────
export async function fetchDailyFocus(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/signals${query}`, {}, getToken);
}

export async function addToPriorityQueue(unitId = 'all', item, getToken) {
  const result = await apiFetch('/api/queue', { method: 'POST', body: JSON.stringify({ ...item, unitId }) }, getToken);
  window.dispatchEvent(new Event('nexonurse:queue-changed'));
  return result;
}

// ─── PERFORMANCE BUCKETS ──────────────────────────────────────────────────────
export async function fetchPerformanceBuckets({ level, marketId, areaId } = {}, getToken) {
  const params = new URLSearchParams();
  if (level) params.set('level', level);
  if (marketId) params.set('marketId', marketId);
  if (areaId) params.set('areaId', areaId);
  const qs = params.toString() ? `?${params}` : '';
  return apiFetch(`/api/metrics/performance-buckets${qs}`, {}, getToken);
}

// ─── INITIATIVES ──────────────────────────────────────────────────────────────
export async function fetchInitiatives(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/initiatives${query}`, {}, getToken);
}

export async function createInitiative(data, getToken) {
  return apiFetch('/api/initiatives', { method: 'POST', body: JSON.stringify(data) }, getToken);
}

// ─── ASSIGNMENTS ──────────────────────────────────────────────────────────────
export async function fetchAssignments(unitId = 'all', userId, getToken) {
  const params = new URLSearchParams();
  if (unitId && unitId !== 'all') params.set('unitId', unitId);
  params.set('userId', userId);
  return apiFetch(`/api/assignments?${params}`, {}, getToken);
}

export async function createAssignment(data, getToken) {
  return apiFetch('/api/assignments', { method: 'POST', body: JSON.stringify(data) }, getToken);
}

export async function updateAssignmentStatus(id, status, getToken) {
  return apiFetch(`/api/assignments/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }, getToken);
}

// ─── DIRECTOR 6-PILLAR DATA ───────────────────────────────────────────────────
export async function getDirectorDrilldownOptions(getToken) {
  return apiFetch('/api/metrics/director-drilldown-options', {}, getToken);
}

export async function getAccessMetrics(level, scopeId, getToken) {
  return apiFetch(`/api/metrics/director/access?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getQualityMetrics(level, scopeId, getToken) {
  return apiFetch(`/api/metrics/director/quality?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getStaffingMetrics(level, scopeId, getToken) {
  return apiFetch(`/api/metrics/director/staffing?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getFinanceMetrics(level, scopeId, getToken) {
  return apiFetch(`/api/metrics/director/finance?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getInitiativesData(level, scopeId, getToken) {
  return apiFetch(`/api/metrics/director/initiatives?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getConsumerInsights(_level, _scopeId, getToken) {
  return apiFetch(`/api/metrics/director/consumer?scopeId=${encodeURIComponent(_scopeId)}`, {}, getToken);
}

export async function getPerformanceBucketsByScope(scopeId, _pillarType, getToken) {
  return apiFetch(`/api/metrics/performance-buckets-by-scope?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

// ─── FINANCIAL TRACKING (NX-005) ────────────────────────────────────────────
export async function fetchFinancialOverview(unitId = 'all', getToken) {
  return apiFetch(`/api/finance/overview?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchBudgetCards(unitId = 'all', getToken) {
  return apiFetch(`/api/finance/budget-cards?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function submitCSVUpload(_file, getToken) {
  return apiFetch('/api/finance/uploads/csv', { method: 'POST' }, getToken);
}

export async function fetchUploadHistory(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/finance/uploads/history${query}`, {}, getToken);
}

export async function fetchFieldMappings(_cardType, getToken) {
  return apiFetch('/api/finance/field-mappings', {}, getToken);
}

export async function saveFieldMapping(cardType, mappings, getToken) {
  return apiFetch('/api/finance/field-mappings', { method: 'PUT', body: JSON.stringify({ cardType, mappings }) }, getToken);
}

export async function fetchFinanceSignals(unitId = 'all', getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/finance/signals${query}`, {}, getToken);
}

export async function getFinanceDrilldownOptions(getToken) {
  return apiFetch('/api/finance/drilldown-options', {}, getToken);
}

// ─── PRACTICE MANAGEMENT ────────────────────────────────────────────────────

export async function getPMTaskLibrary(unitId, getToken) {
  return apiFetch(`/api/practice-management/tasks?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function getPMPriorityQueue(unitId, date, getToken) {
  return apiFetch(`/api/practice-management/queue?unitId=${encodeURIComponent(unitId)}&date=${encodeURIComponent(date)}`, {}, getToken);
}

export async function getPMChecklists(unitId, getToken) {
  return apiFetch(`/api/practice-management/checklists?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function getPMOverviewMetrics(unitId, getToken) {
  return apiFetch(`/api/practice-management/overview?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function getPMCompletionHistory(unitId, _filters, getToken) {
  return apiFetch(`/api/practice-management/history?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function getPMStaffRoster(unitId, getToken) {
  return apiFetch(`/api/practice-management/staff?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function getDirectorPMMetrics(scopeType, scopeId, getToken) {
  return apiFetch(`/api/practice-management/director?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorPMOrphanedEvents(scopeType, scopeId, getToken) {
  return apiFetch(`/api/practice-management/director/orphaned?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

// ─── PRACTICE MANAGEMENT — MUTATIONS ──────────────────────────────────────

export async function updatePMTask(taskId, updates, getToken) {
  return apiFetch(`/api/practice-management/tasks/${encodeURIComponent(taskId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

export async function createPMTask(task, getToken) {
  return apiFetch('/api/practice-management/tasks', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(task),
  }, getToken);
}

export async function restorePMTask(taskId, getToken) {
  return apiFetch(`/api/practice-management/tasks/${encodeURIComponent(taskId)}/restore`, {
    method: 'POST',
  }, getToken);
}

export async function completePMTask(instanceId, { note, linkedOutputs } = {}, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/complete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note, linkedOutputs }),
  }, getToken);
}

export async function skipPMTask(instanceId, reason, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/skip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, getToken);
}

export async function delegatePMTask(instanceId, delegatee, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/delegate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ delegatee }),
  }, getToken);
}

export async function reassignPMOrphanedTask(instanceId, newOwner, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/reassign`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ newOwner }),
  }, getToken);
}

export async function deferPMOrphanedTask(instanceId, deferDate, reason, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/defer`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deferDate, reason }),
  }, getToken);
}

export async function reorderPMQueue(instanceId, toIndex, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/reorder`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex }),
  }, getToken);
}

export async function updatePMChecklistStep(checklistId, stepId, updates, getToken) {
  return apiFetch(`/api/practice-management/checklists/${encodeURIComponent(checklistId)}/steps/${encodeURIComponent(stepId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

export async function parkPMTask(instanceId, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/park`, {
    method: 'POST',
  }, getToken);
}

export async function schedulePMTask(instanceId, dueDate, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/schedule`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dueDate }),
  }, getToken);
}

export async function queuePMTaskToday(instanceId, getToken) {
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/queue-today`, {
    method: 'POST',
  }, getToken);
}

export async function updatePMChecklistComment(checklistId, comment, getToken) {
  return apiFetch(`/api/practice-management/checklists/${encodeURIComponent(checklistId)}/comment`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment }),
  }, getToken);
}

// ─── PRACTICE MANAGEMENT — WORKFLOWS (DAG) ───────────────────────────────

export async function getPMWorkflows(getToken) {
  return apiFetch('/api/practice-management/workflows', {}, getToken);
}

export async function getPMWorkflow(workflowId, getToken) {
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {}, getToken);
}

export async function createPMWorkflowInstance(workflowId, { activatedAt, dueDate } = {}, getToken) {
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}/instances`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ activatedAt, dueDate }),
  }, getToken);
}

export async function getPMWorkflowInstances(unitId = 'all', workflowId, getToken) {
  const query = unitId && unitId !== 'all' ? `?unitId=${encodeURIComponent(unitId)}` : '';
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}/instances${query}`, {}, getToken);
}

export async function getPMWorkflowInstanceState(instanceId, getToken) {
  return apiFetch(`/api/practice-management/workflows/instances/${encodeURIComponent(instanceId)}`, {}, getToken);
}

export async function selectPMWorkflowGate(instanceId, nodeId, selection, getToken) {
  return apiFetch(`/api/practice-management/workflows/instances/${encodeURIComponent(instanceId)}/gate/${encodeURIComponent(nodeId)}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selection }),
  }, getToken);
}

export async function validatePMWorkflowEdge(workflowId, from, to, getToken) {
  return apiFetch('/api/practice-management/workflows/validate-edge', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workflowId, from, to }),
  }, getToken);
}

export async function createPMWorkflow(workflow, getToken) {
  return apiFetch('/api/practice-management/workflows', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(workflow),
  }, getToken);
}

export async function updatePMWorkflow(workflowId, updates, getToken) {
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

export async function deletePMWorkflow(workflowId, getToken) {
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'DELETE',
  }, getToken);
}

// ─── SCHEDULING & ACCESS (NX-007) ───────────────────────────────────────────

// Manager L1
export async function fetchSchedulingOverview(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/overview?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// Manager L2 — Capacity (FSA.2)
export async function fetchCapacityAnalysis(unitId = 'all', period = 'week', getToken) {
  return apiFetch(`/api/scheduling/capacity?unitId=${encodeURIComponent(unitId)}&period=${encodeURIComponent(period)}`, {}, getToken);
}

// Manager L2 — No-Show (FSA.3)
export async function fetchNoShowSummary(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/noshow/summary?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function fetchNoShowIntelligence(unitId = 'all', _filters = {}, getToken) {
  return apiFetch(`/api/scheduling/noshow/intelligence?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// Manager L2 — Access Lag (FSA.4)
export async function fetchAccessLag(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/access-lag?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// Manager L2 — Referral Aging (FSA.5)
export async function fetchReferralAging(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/referrals?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function assignReferral(referralId, staffId, getToken) {
  return apiFetch(`/api/scheduling/referrals/${encodeURIComponent(referralId)}/assign`, { method: 'POST', body: JSON.stringify({ staffId }) }, getToken);
}

// Manager L2 — Phone & Portal (FSA.6)
export async function fetchPhonePortal(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/phone-portal?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

export async function submitManualPhoneStats(unitId = 'all', data, getToken) {
  return apiFetch(`/api/scheduling/phone-portal/manual?unitId=${encodeURIComponent(unitId)}`, { method: 'POST', body: JSON.stringify(data) }, getToken);
}

// Manager L2 — Prior Auth (FSA.7)
export async function fetchPriorAuth(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/prior-auth?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// Signal Log (FSA.8)
export async function fetchSignalLog(unitId = 'all', getToken) {
  return apiFetch(`/api/scheduling/signals/log?unitId=${encodeURIComponent(unitId)}`, {}, getToken);
}

// Director — Scheduling
export async function getDirectorSchedulingOverview(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/overview?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorSchedulingCapacity(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/capacity?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorSchedulingNoShow(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/noShow?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorSchedulingAccessLag(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/accessLag?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorSchedulingPhonePortal(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/phonePortal?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

export async function getDirectorSchedulingPriorAuth(scopeId, getToken) {
  return apiFetch(`/api/scheduling/director/priorAuth?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken);
}

// ─── CONTEXT STORE ──────────────────────────────────────────────────────────
export async function getPracticeContext(getToken) {
  return apiFetch('/api/context/practice', {}, getToken);
}

export async function getPracticeSites(getToken) {
  return apiFetch('/api/context/practice/sites', {}, getToken);
}

// ─── NETWORK PULSE (DIRECTOR) ──────────────────────────────────────────────
export async function fetchNetworkPulseData(getToken) {
  return apiFetch('/api/director/network-pulse', {}, getToken);
}
